import React from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { V4_TABLE } from '../../theme/v4Compat';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Loader } from '../../elements';
import NoDataMessage from '../shared/NoDataMessage';
import { useFarmDateTime } from '../../hooks/useFarmDateTime';
import { AnimalFeedHistoryItem, feedRegistrationDayInstant, formatFeedAmount } from '../../models/feed-registration';
import { LoaderSize } from '../../constants/constants';

export interface AnimalFeedHistoryTableProps {
  items: AnimalFeedHistoryItem[];
  isBusy: boolean;
  loaded: boolean;
}

/**
 * One animal's share of each feeding its pens received.
 *
 * The pen amount and the piglet count sit next to the share on purpose: the share is a division, and
 * a farmer questioning "why 3.33 kg?" needs the two numbers it came from on the same row. Without
 * them the figure is unauditable, and feed is what the farm is billed for.
 *
 * There is no time column - a feeding carries the day it was handed out, not a timestamp.
 */
const AnimalFeedHistoryTable: React.FC<AnimalFeedHistoryTableProps> = ({ items, isBusy, loaded }) => {
  const { t } = useTranslation();
  const { formatDate } = useFarmDateTime();

  if (isBusy && !loaded) return <Loader isLoading size={LoaderSize.MEDIUM} />;

  if (loaded && items.length === 0) {
    return <NoDataMessage text={t('f-feed-reg-animal-empty')} description={t('f-feed-reg-animal-empty-info')} />;
  }

  return (
    <TableContainer className="overflow-x-auto">
      <Table size="small" aria-label="animal feed history table" id="animal-feed-history-table" sx={V4_TABLE}>
        <TableHead>
          <TableRow>
            <TableCell>{t('f-com-date')}</TableCell>
            <TableCell>{t('f-loc-pen')}</TableCell>
            <TableCell>{t('f-feed-reg-name')}</TableCell>
            <TableCell align="right">{t('f-feed-reg-pen-total')}</TableCell>
            <TableCell align="right">{t('f-feed-reg-piglets')}</TableCell>
            <TableCell align="right">{t('f-feed-reg-piglet-share', { defaultValue: 'Per piglet share' })}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item) => (
            // One feeding is split across the pen, so `sourceId` repeats across animals but appears
            // once per animal - the date pins the rest, since a pen can be fed twice in a day.
            <TableRow key={`${item.sourceId}-${item.date}`} hover>
              <TableCell>{formatDate(feedRegistrationDayInstant(item.date))}</TableCell>
              <TableCell>
                <Link to={`/locations/${item.locationId}/feed`} className="text-brand hover:underline">
                  {item.locationNumber}
                </Link>
              </TableCell>
              <TableCell className="font-medium">{item.feedName}</TableCell>
              <TableCell align="right" className="text-gray-500">
                {formatFeedAmount(item.penAmountGrams)}
              </TableCell>
              <TableCell align="right" className="text-gray-500">
                {item.pigletCount}
              </TableCell>
              <TableCell align="right" className="font-semibold">
                {formatFeedAmount(item.allocatedGrams)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default AnimalFeedHistoryTable;
